import { type StructuredContentDocument } from '@repo/contracts/multi-use/structured-content/types';
import { css, cx } from '@repo/pandacss/styled-system/css';
import type { IconType } from 'react-icons';
import { StructuredContentRenderer } from '../../StructuredContentRenderer';
import { ICON_BACKGROUND_SIZE, ICON_SIZE } from './constants';

type Bullet = string | StructuredContentDocument;

type Props = {
  title: string;
  items: Bullet[];
  colorScheme: 'green' | 'red';
  Icon: IconType;
};

const schemeStyles = {
  green: {
    border: css({
      borderTopColor: 'green.lighter',
      borderRightColor: 'green.lighter',
      borderBottomColor: 'green.lighter',
      borderLeftColor: 'green.lighter',
    }),
    header: css({ color: 'green.dark', backgroundColor: 'green.lightest' }),
    iconChip: css({ backgroundColor: 'green.lightest', color: 'green.main' }),
  },
  red: {
    border: css({
      borderTopColor: 'red.lighter2',
      borderRightColor: 'red.lighter2',
      borderBottomColor: 'red.lighter2',
      borderLeftColor: 'red.lighter2',
    }),
    header: css({ color: 'red.dark', backgroundColor: 'red.lightest' }),
    iconChip: css({ backgroundColor: 'red.lightest', color: 'red.main' }),
  },
};

const columnClass = css({
  flexGrow: '[1]',
  flexShrink: '[1]',
  flexBasis: '[0%]',
  borderTopWidth: 'thin',
  borderRightWidth: 'thin',
  borderBottomWidth: 'thin',
  borderLeftWidth: 'thin',
  borderTopStyle: 'solid',
  borderRightStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderTopLeftRadius: '3',
  borderTopRightRadius: '3',
  borderBottomRightRadius: '3',
  borderBottomLeftRadius: '3',
});

const headerClass = css({
  textStyle: 'textBold',
  display: 'block',
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  paddingBlockStart: '2',
  paddingBlockEnd: '2',
  borderTopLeftRadius: '3',
  borderTopRightRadius: '3',
});

const listClass = css({
  paddingTop: '4',
  paddingRight: '4',
  paddingBottom: '4',
  paddingLeft: '4',
  '& > *:not(:last-child)': { marginBottom: '4' },
});

const listItemClass = css({
  display: 'flex',
  alignItems: 'flex-start',
  columnGap: '2',
  rowGap: '2',
  paddingTop: '0',
  paddingRight: '0',
  paddingBottom: '0',
  paddingLeft: '0',
});

const iconChipBaseClass = css({
  display: 'grid',
  alignItems: 'center',
  justifyItems: 'center',
  borderTopLeftRadius: 'half',
  borderTopRightRadius: 'half',
  borderBottomRightRadius: 'half',
  borderBottomLeftRadius: 'half',
  marginTop: '1',
  flexShrink: '0',
});

export function PanelColumn({ title, items, colorScheme, Icon }: Props) {
  const scheme = schemeStyles[colorScheme];

  return (
    <div className={cx(columnClass, scheme.border)}>
      <p className={cx(headerClass, scheme.header)}>{title}</p>
      <ul className={listClass}>
        {items.map((bullet, index) => (
          <li key={`${title.toLowerCase()}-bullet-${index}`} className={listItemClass}>
            <span
              className={cx(iconChipBaseClass, scheme.iconChip)}
              style={{ minWidth: ICON_BACKGROUND_SIZE, minHeight: ICON_BACKGROUND_SIZE }}
              aria-hidden="true"
            >
              <Icon style={{ width: ICON_SIZE, height: ICON_SIZE }} />
            </span>
            {typeof bullet === 'string' ? (
              <span>{bullet}</span>
            ) : (
              <StructuredContentRenderer doc={bullet} />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
